import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Activity, ProjectRecord, RoutineItem } from '@jarvis/shared';
import { APP_STAGE, APP_VERSION } from '@jarvis/shared/constants';
import { Ambient } from '../components/Ambient';
import { Hologram } from '../components/hud/Hologram';
import { HudStage } from '../components/hud/Stage';
import { Clock, HBars, Led, LineChart, MiniPie, Panel, Pie, RingGauge, TriangleHolo, useNow, VBars } from '../components/hud/widgets';
import { Icon, type IconName } from '../components/Icon';
import { LearnedNotes } from '../components/LearnedNotes';
import { chat, useChat } from '../lib/ai';
import { fetchHealth } from '../lib/api';
import { fmt } from '../lib/format';
import { google, useGoogle } from '../lib/google';
import { connections, useApiStatus } from '../lib/health';
import { prefsStore, STATE_MODE_LABEL, STATE_MODES, usePrefs, type StateMode } from '../lib/prefs';
import { useLive } from '../lib/useLive';
import { useResource } from '../lib/useResource';
import { answerLocally, LOCAL_COMMANDS, useVoiceState, voice, type LocalFacts } from '../lib/voice';

/* Tela inicial em forma de HUD, composta a partir da imagem de referência.
   Cada instrumento mostra um dado real do Jarvis; o que ainda não tem fonte
   aparece apagado e diz em que etapa acende. Nada aqui é inventado. */

const NAV: { href: string; icon: IconName; label: string }[] = [
  { href: '#/conversa', icon: 'chat', label: 'Conversa' },
  { href: '#/noticias', icon: 'news', label: 'Notícias' },
  { href: '#/email', icon: 'mail', label: 'E-mail' },
  { href: '#/agenda', icon: 'calendar', label: 'Agenda' },
  { href: '#/projetos', icon: 'folder', label: 'Projetos' },
  { href: '#/agentes', icon: 'agent', label: 'Agentes' },
  { href: '#/configuracoes', icon: 'settings', label: 'Configurações' }
];

/* Latência real até o servidor do Jarvis, medida a cada 20 s. */
function useLatency(enabled: boolean) {
  const [hist, setHist] = useState<number[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const ping = async () => {
      const t0 = performance.now();
      try {
        await fetchHealth(AbortSignal.timeout(10_000));
        if (alive) setHist((h) => [...h.slice(-13), Math.round(performance.now() - t0)]);
      } catch { /* a falha já aparece no estado do servidor */ }
    };
    ping();
    const id = window.setInterval(ping, 20_000);
    return () => { alive = false; window.clearInterval(id); };
  }, [enabled]);
  return hist;
}

interface Line { id: number; who: 'voce' | 'jarvis'; text: string; known?: boolean }

/* Console de comando: falar ou escrever. Com a IA configurada no servidor, conversa
   de verdade (a resposta chega em pedaços); sem ela, só comandos locais, e diz isso. */
function CommandDeck({ facts, aiOn, name }: { facts: LocalFacts; aiOn: boolean; name: string }) {
  const v = useVoiceState();
  const c = useChat();
  const [text, setText] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [speakBack, setSpeakBack] = useState(true);
  const [help, setHelp] = useState(false);
  const seq = useRef(0);
  const factsRef = useRef(facts);
  factsRef.current = facts;
  const speakRef = useRef(speakBack);
  speakRef.current = speakBack;

  const push = (...add: Omit<Line, 'id'>[]) => setLines((l) => [...l, ...add.map((x) => ({ ...x, id: ++seq.current }))].slice(-4));

  const handle = async (said: string) => {
    const q = said.trim();
    if (!q || chat.get().streaming) return;
    setText('');
    if (aiOn) {
      push({ who: 'voce', text: q });
      /* A voz começa já na primeira frase, enquanto o resto da resposta chega. */
      const sp = speakRef.current ? voice.speaker() : null;
      const reply = await chat.send(q, name, { voice: Boolean(sp), onDelta: (t) => sp?.push(t) });
      sp?.end();
      const err = chat.get().error;
      if (reply) push({ who: 'jarvis', text: reply });
      else if (err) push({ who: 'jarvis', text: err.message, known: false });
      return;
    }
    const a = answerLocally(q, factsRef.current);
    push({ who: 'voce', text: q }, { who: 'jarvis', text: a.text, known: a.known });
    if (speakRef.current) voice.speak(a.text);
  };

  useEffect(() => {
    if (!v.final) return;
    const said = v.final;
    voice.clear();
    void handle(said);
  }, [v.final]);

  const submit = (e: FormEvent) => { e.preventDefault(); void handle(text); };
  const micTitle = !v.supported.listen ? 'Este navegador não reconhece fala. Use Chrome, Edge ou Safari.'
    : v.listening ? 'Parar de escutar' : 'Falar com o Jarvis';
  const last = lines.slice(-2);
  const st = c.status;
  const lastMsg = c.messages.at(-1);
  const lastSources = lastMsg?.role === 'assistant' ? lastMsg.sources?.length ?? 0 : 0;

  return (
    <section className="hx-w hx-deck" aria-label="Falar com o Jarvis">
      <div className="deck-lines" aria-live="polite">
        {last.length === 0 && !v.listening && !c.streaming && (
          <p className="deck-hint">{aiOn ? 'Toque no globo ou no microfone e fale com o Jarvis. Ou escreva abaixo.' : 'Toque no globo ou no microfone e fale. Ou escreva abaixo.'}</p>
        )}
        {v.listening && <p className="deck-line voce"><span className="who">VOCÊ</span><span>{v.interim || 'Escutando…'}</span></p>}
        {!v.listening && c.streaming && (
          <>
            <p className="deck-line voce"><span className="who">VOCÊ</span><span>{c.pendingQuestion}</span></p>
            <p className="deck-line jarvis live"><span className="who">JARVIS</span><span>{c.draft || (c.searching ? `pesquisando na web: “${c.searching}”…` : 'pensando…')}</span></p>
          </>
        )}
        {!v.listening && !c.streaming && last.map((l) => (
          <p key={l.id} className={`deck-line ${l.who} ${l.known === false ? 'unknown' : ''}`}>
            <span className="who">{l.who === 'voce' ? 'VOCÊ' : 'JARVIS'}</span><span>{l.text}</span>
          </p>
        ))}
      </div>

      <form className="deck-bar" onSubmit={submit} autoComplete="off">
        <span className="deck-prompt">JM&gt;</span>
        <label htmlFor="deck-input" className="sr-only">Mensagem para o Jarvis</label>
        <input id="deck-input" className="deck-input" maxLength={4000}
          placeholder={v.listening ? 'Escutando…' : aiOn ? 'Fale ou escreva com o Jarvis…' : 'Fale ou escreva um comando…'}
          value={v.listening ? v.interim : text} readOnly={v.listening}
          onChange={(e) => setText(e.target.value)} />
        {v.speaking
          ? <button type="button" className="deck-btn" onClick={() => voice.hush()} title="Parar a fala" aria-label="Parar a fala"><Icon name="pause" /></button>
          : <button type="button" className={`deck-btn mic ${v.listening ? 'live' : ''}`} disabled={!v.supported.listen || c.streaming}
              title={micTitle} aria-label={micTitle} aria-pressed={v.listening}
              onClick={() => (v.listening ? voice.stop() : voice.listen())}><Icon name="mic" /></button>}
        {c.streaming
          ? <button type="button" className="deck-btn send" title="Parar a resposta" aria-label="Parar a resposta" onClick={() => chat.stop()}><Icon name="pause" /></button>
          : <button type="submit" className="deck-btn send" title="Enviar" aria-label="Enviar"><Icon name="send" /></button>}
      </form>

      <div className="deck-foot">
        {aiOn && st
          ? <span><Icon name="brain" />Claude conectado · {fmt.num(st.today.tokens)} de {fmt.num(st.limits.daily)} tokens hoje</span>
          : <span><Icon name="lock" />Sem modelo de IA no servidor: só comandos locais.</span>}
        {aiOn
          ? <a className="deck-link" href="#/conversa">abrir conversa</a>
          : <button type="button" className="deck-link" aria-expanded={help} onClick={() => setHelp((h) => !h)}>{help ? 'ocultar comandos' : 'ver comandos'}</button>}
        <label className="deck-toggle"><input type="checkbox" checked={speakBack} onChange={(e) => { setSpeakBack(e.target.checked); if (!e.target.checked) voice.hush(); }} /><span>responder falando</span></label>
      </div>
      {help && !aiOn && (
        <ul className="deck-help">
          {LOCAL_COMMANDS.map((cmd) => <li key={cmd}><button type="button" onClick={() => void handle(cmd)}>“{cmd}”</button></li>)}
        </ul>
      )}
      {aiOn && !c.streaming && lastSources > 0 && lines.at(-1)?.who === 'jarvis' && (
        <p className="deck-mem"><Icon name="search" />Resposta com {lastSources} {lastSources === 1 ? 'fonte' : 'fontes'} da web. <a href="#/conversa">Ver as fontes</a></p>
      )}
      {aiOn && !c.streaming && <LearnedNotes compact />}
      {c.suggestions.some((s) => s.state === 'new') && (
        <p className="deck-mem"><Icon name="brain" />O Jarvis sugeriu algo para lembrar. <a href="#/conversa">Revisar na conversa</a></p>
      )}
      {v.error && <p className="deck-err" role="alert">{v.error}</p>}
      {v.supported.listen && <p className="deck-privacy">No Chrome e no Edge, o áudio do microfone é enviado ao fabricante do navegador para ser transcrito.</p>}
    </section>
  );
}
/* Circuitos da composição: cada painel ligado ao holograma, como na referência. */
function traces(W: number, H: number): [number, number][][] {
  const c = W / 2, m = H / 2;
  const gx = c - 336, gy = 214;
  return [
    [[378, 118], [gx - 60, 118], [gx - 44, gy - 46]],
    [[gx + 64, gy], [gx + 120, gy], [gx + 150, gy + 40]],
    [[378, 262], [430, 262], [470, 302], [c - 300, 302]],
    [[400, 386], [430, 386], [456, 412]],
    [[234, 450], [270, 450], [300, m + 50]],
    [[340, H - 126], [340, H - 150], [380, H - 190], [c - 330, H - 190]],
    [[c + 206, 230], [c + 180, 230], [c + 150, 262]],
    [[W - 532, 366], [W - 560, 366], [W - 590, 396]],
    [[W - 182, 440], [W - 226, 440], [W - 262, 476], [W - 300, 476]],
    [[W - 420, m + 116], [W - 420, m + 92], [W - 440, m + 72]],
    [[W - 213, H - 126], [W - 213, H - 150], [W - 240, H - 176], [W - 300, H - 176]]
  ];
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export function HomeHud() {
  const { displayName, stateMode } = usePrefs();
  const api = useApiStatus();
  const up = api.state === 'up';
  const gs = useGoogle();
  useEffect(() => { if (up) void google.load(); }, [up]);
  /* Google ligado = sua conta conectada de fato, não só credenciais no servidor. */
  const conn = { ...connections(api), google: Boolean(gs?.connected && !gs.needsReconnect) };
  const demo = stateMode === 'demo';
  const v = useVoiceState();
  const now = useNow(60_000);

  const overview = useResource('overview');
  const demoProjects = useResource('projects');
  const projects = useLive('/api/projects', (b: { items: ProjectRecord[] }) => b.items);
  const routine = useLive('/api/routine', (b: { items: RoutineItem[] }) => b.items);
  const activity = useLive('/api/activity?limit=200', (b: { items: Activity[] }) => b.items);
  const latency = useLatency(up);

  const ov = overview.state.status === 'ready' ? overview.state.response.data : null;

  /* Projetos: os seus de verdade; no modo demonstração, os fictícios marcados. */
  const projList: { name: string; pct: number | null; open: number }[] = demo
    ? (demoProjects.state.status === 'ready' ? demoProjects.state.response.data ?? [] : [])
        .map((p) => ({ name: p.name, pct: p.progress, open: p.tasks.filter((t) => !t.done).length }))
    : (projects.state.status === 'ready' ? projects.state.data : [])
        .filter((p) => p.status === 'active')
        .map((p) => ({
          name: p.name,
          pct: p.tasks.length ? (p.tasks.filter((t) => t.done).length / p.tasks.length) * 100 : null,
          open: p.tasks.filter((t) => !t.done).length
        }));
  const projReady = demo ? demoProjects.state.status === 'ready' : projects.state.status === 'ready';
  const openTasks = projList.reduce((s, p) => s + p.open, 0);

  const routineItems = routine.state.status === 'ready' ? [...routine.state.data].sort((a, b) => a.time.localeCompare(b.time)) : [];
  const hourBars = Array.from({ length: 18 }, (_, i) => {
    const h = i + 6;
    const n = routineItems.filter((r) => Number(r.time.slice(0, 2)) === h).length;
    return n ? 0.35 + Math.min(1, n * 0.4) : 0.06;
  });
  const nowHHMM = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
  const nextRoutine = routineItems.find((r) => r.time >= nowHHMM);

  /* Atividade dos últimos 14 dias, do registro real de alterações. */
  const actItems = activity.state.status === 'ready' ? activity.state.data : [];
  const days = Array.from({ length: 14 }, (_, i) => { const d = new Date(now); d.setDate(d.getDate() - 13 + i); return dayKey(d); });
  const actSeries = days.map((k) => actItems.filter((a) => dayKey(new Date(a.at)) === k).length);
  const actTotal = actSeries.reduce((s, n) => s + n, 0);

  const sources = [
    { label: 'Banco de dados', on: conn.banco },
    { label: 'Login 2 fatores', on: conn.login },
    { label: 'Modelo de IA', on: conn.ia },
    { label: 'Notícias (etapa 3)', on: false },
    { label: 'Gmail (leitura)', on: conn.google },
    { label: 'Google Agenda (etapa 5)', on: conn.google }
  ];
  const lit = sources.filter((s) => s.on).length;
  const connCount = [conn.banco, conn.login, conn.ia, conn.google].filter(Boolean).length;

  /* Consumo de IA: real (servidor) ou, no modo demonstração, o fictício marcado. */
  const aiStatus = useChat().status;
  useEffect(() => { if (up) void chat.loadStatus(); }, [up]);
  const aiOn = Boolean(aiStatus?.configured);
  const usage = demo
    ? ov?.usage ?? null
    : aiStatus?.configured
      ? { tokensToday: aiStatus.today.tokens, dailyLimit: aiStatus.limits.daily, tokensMonth: aiStatus.month.tokens, monthlyLimit: aiStatus.limits.monthly, requestsToday: aiStatus.today.requests }
      : null;
  const iaPct = usage ? (usage.tokensToday / usage.dailyLimit) * 100 : null;
  const pending = ov?.pending.length ?? 0;
  const jobs = ov?.jobs ?? [];
  const lastLat = latency.at(-1);

  const facts: LocalFacts = {
    eventosHoje: ov ? ov.events.length : undefined,
    aguardando: pending,
    projetosAtivos: projReady ? projList.length : undefined,
    conectado: conn
  };

  const voiceState = v.listening ? 'OUVINDO' : v.speaking ? 'FALANDO' : !up ? (api.state === 'checking' ? 'CONECTANDO' : 'SERVIDOR OFF') : 'EM ESPERA';
  const coreLabel = !v.supported.listen ? 'Globo do Jarvis (este navegador não reconhece fala)' : v.listening ? 'Parar de escutar' : 'Falar com o Jarvis';
  const onCore = () => { if (!v.supported.listen) return; if (v.listening) voice.stop(); else void voice.listen(); };

  useEffect(() => { document.documentElement.dataset.hud = 'on'; return () => { delete document.documentElement.dataset.hud; }; }, []);

  return (
    <>
    <Ambient hud />
    <HudStage label="Visão geral do Jarvis" className="hx-home" traces={traces}>

      {/* ---------- Título ---------- */}
      <header className="hx-w hx-title">
        <h1>JARVIS <span>MASTER</span></h1>
        <p>{fmt.greeting(now)}{displayName ? `, ${displayName}` : ''} · Etapa {APP_STAGE} · {demo ? 'modo demonstração' : 'dados reais'}</p>
      </header>
      {demo && <a className="hx-w hx-demo" href="#/configuracoes/laboratorio">DEMONSTRAÇÃO · dados fictícios</a>}

      <Clock />

      {/* ---------- Servidor: latência real ---------- */}
      <Panel title="Servidor" className="hx-server" tag={up ? 'no ar' : api.state === 'checking' ? '…' : 'desligado'}>
        <div className="srv-row">
          <span className="srv-leds" aria-hidden="true"><i className={up ? 'on' : ''} /><i className={conn.banco ? 'on' : ''} /><i className={conn.login ? 'on' : ''} /></span>
          <b className="srv-big">{lastLat != null ? lastLat : '—'}<small>ms</small></b>
          <span className="srv-cap">latência</span>
        </div>
        <VBars className="srv-hist" values={latency.length ? latency.map((l) => Math.min(1, l / 1500)) : Array(14).fill(0)} />
        <div className="srv-lines">
          <span><i className="bar" style={{ width: up ? '100%' : '8%' }} />API</span>
          <span><i className="bar" style={{ width: conn.banco ? '100%' : '8%' }} />banco</span>
          <span><i className="bar" style={{ width: conn.login ? '100%' : '8%' }} />login</span>
        </div>
      </Panel>

      {/* ---------- Consumo de IA ---------- */}
      <div className="hx-w hx-ia">
        <RingGauge value={iaPct} size={128}
          center={iaPct != null ? <><b>{Math.round(iaPct)}<small>%</small></b><span>IA hoje</span></> : <><b>—</b><span>IA off</span></>}
          caption={usage ? `${fmt.num(usage.tokensToday)} tokens` : 'sem chave no servidor'} />
      </div>

      {/* ---------- Comando de voz ---------- */}
      <section className="hx-w hx-voice" aria-label="Comando de voz">
        <button type="button" className={`vc-orb ${v.listening ? 'live' : ''}`} onClick={onCore} disabled={!v.supported.listen} aria-label={coreLabel} title={coreLabel}>
          <Icon name="mic" />
        </button>
        <div className="vc-text">
          <b>comando de voz</b>
          <span>{voiceState}</span>
        </div>
        <ul className="vc-lines">
          <li>Escuta: {v.supported.listen ? 'pt-BR, do navegador' : 'indisponível aqui'}</li>
          <li>Fala: {v.supported.speak ? 'voz local do aparelho' : 'indisponível'}</li>
          <li>Raciocínio: comandos locais</li>
        </ul>
      </section>

      {/* ---------- Aguarda você ---------- */}
      <a className={`hx-w hx-pending ${pending ? 'warn' : ''}`} href={pending ? '#/email' : '#/agenda'}>
        <span className="pd-ico"><Icon name="shield" /></span>
        <span className="pd-n">{pad2(pending)}</span>
        <span className="pd-t">{pending ? (pending === 1 ? 'item aguarda você' : 'itens aguardam você') : 'nada aguarda você'}</span>
      </a>

      {/* ---------- Agentes ---------- */}
      <Panel title="Agentes" className="hx-agents" href="#/agentes" tag={jobs.length ? `${jobs.length} tarefas` : 'inativos'}>
        <TriangleHolo dim={!jobs.length} />
        <p className="ag-sub">{jobs.length
          ? `${jobs.filter((j) => j.status === 'running').length} executando · ${jobs.filter((j) => j.status === 'waiting').length} aguardando`
          : 'Nenhum agente em execução. Eles chegam depois do modelo de IA.'}</p>
      </Panel>

      {/* ---------- Uso ---------- */}
      <Panel title="Uso de IA e limites" className="hx-usage" tag={usage ? `${usage.requestsToday} req.` : 'sem IA'}>
        <HBars rows={[
          { label: 'Tokens hoje', value: usage ? usage.tokensToday / usage.dailyLimit : null, text: usage ? `${fmt.num(usage.tokensToday)} / ${fmt.num(usage.dailyLimit)}` : '—' },
          { label: 'Tokens no mês', value: usage ? usage.tokensMonth / usage.monthlyLimit : null, text: usage ? `${fmt.num(usage.tokensMonth)} / ${fmt.num(usage.monthlyLimit)}` : '—' },
          { label: 'Tarefas abertas', value: projReady ? Math.min(1, openTasks / 20) : null, text: projReady ? String(openTasks) : '—' }
        ]} />
      </Panel>

      {/* ---------- Projetos ---------- */}
      <Panel title="Projetos" className="hx-projects" href="#/projetos" tag={projReady ? `${projList.length} ativos` : '…'}>
        <div className="pj-row">
          {projReady && projList.length === 0 && <p className="hx-empty">Nenhum projeto ativo. Crie o primeiro em Projetos.</p>}
          {projList.slice(0, 4).map((p) => (
            <MiniPie key={p.name} value={p.pct} label={p.name} sub={p.pct == null ? 'sem tarefas' : `${Math.round(p.pct)}%`} />
          ))}
        </div>
      </Panel>

      {/* ---------- Equalizador do microfone ---------- */}
      <div className="hx-w hx-eq" aria-hidden="true">
        <VBars live={v.listening || v.speaking} values={Array.from({ length: 12 }, (_, i) => {
          const base = 0.12 + Math.abs(Math.sin((i + 1) * 1.9)) * 0.22;
          return v.listening ? base + v.level * (1 - Math.abs(i - 5.5) / 8) : v.speaking ? base + 0.35 : base * 0.6;
        })} />
        <span>mic</span>
      </div>

      {/* ---------- Conexões ---------- */}
      <div className="hx-w hx-leds" role="group" aria-label="Conexões">
        <Led on={conn.banco} label="Banco" />
        <Led on={conn.login} label="Login" />
        <Led on={conn.ia} label="IA" />
        <Led on={conn.google} label="Google" />
      </div>

      {/* ---------- Holograma central ---------- */}
      <div className="hx-holo">
        <Hologram variant="home" coreLabel={coreLabel} onCore={onCore}
          signal={{ level: v.level, listening: v.listening, speaking: v.speaking, alert: pending > 0, online: up }} />
        <div className="holo-readout" aria-live="polite">
          <span className={`hr-state ${v.listening || v.speaking ? 'live' : ''}`}>{voiceState}</span>
          <span className="hr-sub">{projReady ? `${pad2(projList.length)} projetos · ${pad2(openTasks)} tarefas abertas` : 'carregando seus dados…'}</span>
        </div>
      </div>

      <CommandDeck facts={facts} aiOn={aiOn} name={displayName} />

      {/* ---------- Navegação ---------- */}
      <nav className="hx-w hx-nav" aria-label="Seções">
        {NAV.map((n) => (
          <a key={n.href} href={n.href} title={n.label} aria-label={n.label}><Icon name={n.icon} /><span>{n.label}</span></a>
        ))}
        <label className="hx-state" title="Laboratório de estados: como as telas carregam os dados">
          <span>Estado</span>
          <select aria-label="Modo de estado da interface" value={stateMode} onChange={(e) => prefsStore.set({ stateMode: e.target.value as StateMode })}>
            {STATE_MODES.map((m) => <option key={m} value={m}>{STATE_MODE_LABEL[m]}</option>)}
          </select>
        </label>
      </nav>

      {/* ---------- Fontes ---------- */}
      <Panel title="Fontes de dados" className="hx-sources" href="#/configuracoes/integracoes" tag={`${lit}/${sources.length}`}>
        <div className="src-wrap">
          <Pie slices={sources} size={104} />
          <ul className="src-list">
            {sources.map((s) => <li key={s.label} className={s.on ? 'on' : ''}><i />{s.label}</li>)}
          </ul>
        </div>
      </Panel>

      {/* ---------- Sistema ---------- */}
      <Panel title="Sistema" className="hx-system">
        <dl className="sys">
          <div><dt>versão</dt><dd>{APP_VERSION}</dd></div>
          <div><dt>etapa</dt><dd>{APP_STAGE}</dd></div>
          <div><dt>banco</dt><dd>{up ? api.health.database.kind : '—'}</dd></div>
          <div><dt>modo</dt><dd>{STATE_MODE_LABEL[stateMode].toLowerCase()}</dd></div>
          <div><dt>voz</dt><dd>{v.supported.listen ? 'pronta' : 'sem escuta'}</dd></div>
          <div><dt>build</dt><dd>{__BUILD__}</dd></div>
        </dl>
      </Panel>

      {/* ---------- Conexões em anel ---------- */}
      <div className="hx-w hx-conn">
        <RingGauge value={(connCount / 4) * 100} size={112}
          center={<><b>{connCount}<small>/4</small></b><span>conexões</span></>} />
      </div>

      {/* ---------- Notícias ---------- */}
      <Panel title="Notícias" className="hx-news" href="#/noticias" tag={ov ? `${ov.news.length}` : 'etapa 3'}>
        {ov && ov.news[0]
          ? <p className="nw-t">{ov.news[0].title}<span>{ov.news[0].source.name} · {fmt.relative(ov.news[0].publishedAt)}</span></p>
          : <p className="hx-empty">Nenhuma fonte de notícias conectada.</p>}
      </Panel>

      {/* ---------- Agenda e rotina ---------- */}
      <Panel title="Hoje" className="hx-today" href="#/agenda" tag={ov ? `${ov.events.length} eventos` : 'rotina'}>
        <VBars className="td-hours" values={hourBars} />
        <div className="td-axis" aria-hidden="true"><span>6h</span><span>12h</span><span>18h</span><span>23h</span></div>
        {ov && ov.events.length > 0
          ? <ul className="td-list">{ov.events.slice(0, 3).map((e) => <li key={e.id}><b>{fmt.time(e.start)}</b>{e.title}</li>)}</ul>
          : routineItems.length
            ? <ul className="td-list">{routineItems.slice(0, 3).map((r) => <li key={r.id} className={r === nextRoutine ? 'next' : ''}><b>{r.time}</b>{r.label}</li>)}</ul>
            : <p className="hx-empty">{routine.state.status === 'loading' ? 'Carregando rotina…' : 'Sem rotina definida. A Agenda Google entra na etapa 5.'}</p>}
      </Panel>

      {/* ---------- Atividade ---------- */}
      <Panel title="Atividade · 14 dias" className="hx-activity" href="#/configuracoes" tag={activity.state.status === 'ready' ? `${actTotal} alterações` : '…'}>
        <LineChart values={actSeries} w={320} h={72} />
      </Panel>
    </HudStage>
    </>
  );
}
